import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Briefcase, ChevronDown } from "lucide-react";
import isEmpty from "../utils/isEmpty";

const AgentAboutCard = ({ AgentData }) => {
  const navigate = useNavigate();
  const [showMoreAbout, setShowMoreAbout] = useState(false);
  const [showMoreCommunity, setShowMoreCommunity] = useState(false);
  const aboutTextRef = useRef(null);
  const [shouldShowAboutToggle, setShouldShowAboutToggle] = useState(false);

  const responce = isEmpty(AgentData);
  const firstName = AgentData?.first_name ?? "Agent";

  const calculateYearsFromCreatedAt = (dateValue) => {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return 0;
    const years = Math.ceil(diffMs / (365.25 * 24 * 60 * 60 * 1000));
    return Math.max(1, years);
  };

  const experienceFromCreatedAt = calculateYearsFromCreatedAt(
    AgentData?.createdAt ?? AgentData?.created_at,
  );

  const experience =
    AgentData?.years_experience ??
    AgentData?.experience ??
    AgentData?.years_of_experience ??
    (experienceFromCreatedAt != null ? experienceFromCreatedAt : "23");

  const experienceText =
    typeof experience === "number"
      ? `${experience} years experience`
      : String(experience)
            .replace(/\s*experience\s*$/i, "")
            .trim()
        ? `${experience}`
        : `${experience} years experience`;

  const aboutText =
    AgentData?.about_me ||
    "Top listing and selling agent for Broadbeach and the surrounding Gold Coast areas. Coastal has five offices across the Gold Coast and has been helping buyers and sellers in the region for over 30 years.";

  const specialtiesText =
    AgentData?.specialties ||
    (Array.isArray(AgentData?.specialties)
      ? AgentData.specialties.join(" ")
      : "Coastal has five offices across the Gold Coast and has been helping buyers and sellers in the region for over 30 years.");

  const communityRaw = AgentData?.community_involvement;
  const communityList = Array.isArray(communityRaw)
    ? communityRaw
    : typeof communityRaw === "string" && communityRaw
      ? communityRaw
          .split(/\n|\.\s+/)
          .filter(Boolean)
          .map((s) => s.trim())
      : [
          "Major supporter of the Broadbeach Bowls Club",
          "Major supporter of the Broadbeach Seniors Club",
          "Major supporter of Broadbeach State School",
        ];
  const communityVisible = showMoreCommunity
    ? communityList
    : communityList.slice(0, 3);

  const aboutImage =
    AgentData?.about_image ??
    AgentData?.coverProfileImg ??
    AgentData?.profileImg;
  const videoUrl = AgentData?.video_URL;

  useEffect(() => {
    if (!aboutTextRef.current) return;
    if (showMoreAbout) return;

    const el = aboutTextRef.current;
    const next = el.scrollHeight > el.clientHeight + 1;

    setShouldShowAboutToggle(next);
  }, [AgentData?.about_me, showMoreAbout]);

  if (responce) return null;

  return (
    <div
      className="bg-white p-4 md:p-6 rounded-xl overflow-hidden"
      style={{ boxShadow: "0px 1px 4px 0px rgba(0,0,0,0.16)" }}
    >
      <div className="flex flex-col md:flex-row md:justify-between md:items-start gap-4 mb-4">
        <div>
          <h3 className="text-[#171717] font-bold text-xl md:text-2xl">
            About {firstName}
          </h3>
          <div className="flex items-center gap-2 mt-1.5 text-[#737373] text-sm">
            <Briefcase className="w-4 h-4 flex-shrink-0" strokeWidth={2} />
            <span>{experienceText}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate(`/appraisal/${AgentData?._id}`)}
          className="rounded-full bg-[#097FE6] text-white text-sm font-medium py-2.5 px-4 whitespace-nowrap flex-shrink-0"
        >
          Request a free appraisal
        </button>
      </div>

      <div className="text-[#525252] text-base leading-relaxed">
        <p ref={aboutTextRef} className={showMoreAbout ? "" : "line-clamp-3"}>{aboutText}</p>
        {shouldShowAboutToggle && (
          <button
            type="button"
            onClick={() => setShowMoreAbout((p) => !p)}
            className="flex items-center gap-1 mt-2 text-[#097FE6] text-sm font-medium hover:underline"
          >
            {showMoreAbout ? "Show less" : "Show more"}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showMoreAbout ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>

      <div className="mt-6">
        <h4 className="text-[#171717] font-bold text-base md:text-lg">
          Specialties
        </h4>
        <p className="text-[#525252] text-base mt-1 leading-relaxed">
          {specialtiesText}
        </p>
      </div>

      <div className="mt-6 rounded-xl overflow-hidden bg-[#F5F5F5]">
        {aboutImage ? (
          <img
            src={aboutImage}
            alt=""
            className="w-full aspect-[16/10] object-cover"
          />
        ) : videoUrl ? (
          <iframe
            title="Agent video"
            width="100%"
            height="340"
            src={`https://www.youtube.com/embed/${String(videoUrl).slice(32, 43)}`}
            className="w-full aspect-video"
            allowFullScreen
          />
        ) : null}
      </div>

      <div className="mt-6">
        <h4 className="text-[#171717] font-bold text-base md:text-lg">
          Community involvement
        </h4>
        <ul className="mt-2 space-y-2 list-none pl-0">
          {communityVisible.map((item, index) => (
            <li
              key={index}
              className="flex items-start gap-2 text-[#525252] text-base"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#171717] flex-shrink-0 mt-2" />
              <span>{item}</span>
            </li>
          ))}
        </ul>
        {communityList.length > 3 && (
          <button
            type="button"
            onClick={() => setShowMoreCommunity((p) => !p)}
            className="flex items-center gap-1 mt-2 text-[#097FE6] text-sm font-medium hover:underline"
          >
            {showMoreCommunity ? "Show less" : "Show more"}
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showMoreCommunity ? "rotate-180" : ""}`}
            />
          </button>
        )}
      </div>
    </div>
  );
};

export default AgentAboutCard;
